import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { ArrowRightIcon, HandCoinsIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';

export type ApplicationsSummaryCardProps = {
  /** Total applications submitted. */
  total: number;
  /** Subset still waiting on a lender decision. */
  pending: number;
  /** Subset with at least one approval (incl. funded). */
  approved: number;
  /** Subset that has been funded. */
  funded: number;
  /** Click handler on the primary "Apply now" CTA. */
  onApply: () => void;
  /** Click handler on the secondary "View applications" link. */
  onViewApplications?: () => void;
  /**
   * Hide the primary "Apply now" CTA entirely. Use when an upstream
   * feature flag (e.g. `financing_module.sub_carrier_apply`) blocks
   * new applications but the stats card should still render so
   * carriers can see their existing pipeline.
   */
  disableApply?: boolean;
  className?: string;
};

const Stat = ({ value, label }: { value: number; label: string }) => (
  <div className="text-center">
    <div className="text-2xl font-bold text-gray-900">{value}</div>
    <div className="mt-0.5 text-xs uppercase tracking-wide text-gray-500">{label}</div>
  </div>
);

/**
 * Compact summary card sized for the carrier `/financing` dashboard
 * tab. Shows at-a-glance application counts + an Apply CTA so the
 * top of the dashboard answers two questions in one card:
 *
 *  - "Where are my open applications?"
 *  - "How do I start a new one?"
 *
 * Reusable from a tighter card slot (e.g. a Home dashboard widget)
 * by passing the same props.
 */
export function ApplicationsSummaryCard({
  total,
  pending,
  approved,
  funded,
  onApply,
  onViewApplications,
  disableApply = false,
  className,
}: ApplicationsSummaryCardProps) {
  const { t } = useTranslation('m-financing');

  return (
    <section
      className={cn(
        'rounded-2xl border border-gray-200 bg-white p-5 sm:p-6',
        className,
      )}>
      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-100">
            <HandCoinsIcon className="size-5" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
              {t('dashboard.summary.title')}
            </h2>
            <p className="mt-0.5 text-sm text-gray-600">
              {t('dashboard.summary.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onViewApplications && total > 0 && (
            <Button variant="outline" type="button" onClick={onViewApplications}>
              {t('dashboard.summary.view_applications')}
            </Button>
          )}
          {!disableApply && (
            <Button
              type="button"
              onClick={onApply}
              className="bg-emerald-700 text-white hover:bg-emerald-800">
              {t('dashboard.summary.apply_cta')}
              <ArrowRightIcon className="ms-2 size-4 rtl:rotate-180" />
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-4 gap-3 border-t border-gray-100 pt-5">
        <Stat value={total} label={t('dashboard.summary.stats.total')} />
        <Stat value={pending} label={t('dashboard.summary.stats.pending')} />
        <Stat value={approved} label={t('dashboard.summary.stats.approved')} />
        <Stat value={funded} label={t('dashboard.summary.stats.funded')} />
      </div>
    </section>
  );
}
